import { Country, SponsoredContentArticlesParams, Term, TermId } from './entities';
import { ListenableSubscribable } from '@benzinga/subscribable';
import { StoryObject } from '@benzinga/advanced-news-manager';

interface TermEvent {
  type: 'content:add_term';
  term: Term;
}

interface TermsUpdateEvent {
  type: 'content:updated_terms' | 'content:add_terms';
  terms: Term[];
}

interface SponsoredNodesUpdateEvent {
  type: 'content:updated_sponsored_nodes' | 'content:add_sponsored_nodes';
  sponsoredNodes: StoryObject[];
}

export type ContentStoreEvent = TermEvent | TermsUpdateEvent | SponsoredNodesUpdateEvent;

export class ContentStore extends ListenableSubscribable<ContentStoreEvent> {
  private countries?: Country[];
  private date?: Date;
  private terms?: Map<TermId, Term>;
  private countriesMap?: Map<number | string, Country>;
  private sponsoredNodes?: Map<string, StoryObject[]>;

  constructor() {
    super();
    this.date = undefined;
    this.terms = undefined;
    this.sponsoredNodes = undefined;
    this.countries = undefined;
  }

  public shouldFetchTerm = (termId: TermId): boolean => {
    const TWELVE_HOURS = 432 * 100000; /* ms */
    const lastCalled = this.date?.getTime() ?? 0;
    if (this.terms === undefined || !this.getTermById(termId) || Date.now() - lastCalled > TWELVE_HOURS) {
      return true;
    }
    return false;
  };

  public shouldFetchSponsoredNodes = (options?: SponsoredContentArticlesParams): boolean => {
    const FIFTEEN_MINUTES = 90 * 10000; /* ms */
    const lastCalled = this.date?.getTime() ?? 0;
    if (
      this.sponsoredNodes === undefined ||
      !this.getSponsoredNodes(options) ||
      Date.now() - lastCalled > FIFTEEN_MINUTES
    ) {
      return true;
    }
    return false;
  };

  public getTermById = (termId: TermId): Term | undefined => {
    return this.terms?.get(String(termId));
  };

  public getTerms = (): Map<TermId, Term> | undefined => {
    return this.terms;
  };

  public setTerm = (term: Term | Term[]): void => {
    this.date = new Date();

    if (!this.terms) {
      this.terms = new Map();
    }

    if (Array.isArray(term)) {
      term.forEach(termItem => {
        this.terms = this.terms?.set(termItem.tid, termItem);
      });

      this.dispatch({
        terms: term,
        type: 'content:add_terms',
      });
    } else {
      this.terms = this.terms.set(term.tid, term);

      this.dispatch({
        term: term,
        type: 'content:add_term',
      });
    }

    this.dispatch({
      terms: [...this.terms.values()],
      type: 'content:updated_terms',
    });
  };

  public setSponsoredNodes = (nodes: StoryObject[], options?: SponsoredContentArticlesParams) => {
    this.date = new Date();

    if (Array.isArray(nodes)) {
      if (!this.sponsoredNodes) {
        this.sponsoredNodes = new Map();
      }
      this.sponsoredNodes?.set(JSON.stringify(options || {}), nodes);

      this.dispatch({
        sponsoredNodes: nodes,
        type: 'content:add_sponsored_nodes',
      });
    }
  };

  public getSponsoredNodes = (options?: SponsoredContentArticlesParams): StoryObject[] | undefined => {
    return this.sponsoredNodes?.get(JSON.stringify(options || {}));
  };
}

export default ContentStore;
